import { useCallback, useEffect, useState } from "react";
import { Listarusers } from "../../../Service/Service.users";
import usePermissions from "../../../hook/usePermissions";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, tituloDeNombre } from "../../UI/DataTable/piezas";
import MenuAcciones from "../../UI/MenuAcciones";
import { CampoTexto } from "../../UI/Campo";
import { IconoEditar, IconoBuscar } from "../../UI/Iconos";

function UserListado({data}) {
    // RBAC-CUTOVER-07: Editar depende de users.edit_personal (backend ya lo
    // exige en users.routes.ts).
    //
    // SISTEMA-DE-DISENO: el icono de Eliminar se retira. NO se retira una
    // funcionalidad: `handleDelete` era una función VACÍA y el icono ya salía
    // apagado -- un botón que nunca hizo nada. Cuando exista el borrado de
    // personal, vuelve como acción del menú, con su permiso y su confirmación.
    const { hasPermission } = usePermissions();

    const { tabs, handleShowEdit, refreshKey } = data;

    const [loading, setLoading] = useState(false);
    const [listarUsers, setListarUsers] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    // ADMINISTRATIVO-BUSCADOR-01: la pantalla se pagina en SERVIDOR (10 por
    // pagina) y no tenia buscador: con 58 usuarios, encontrar a alguien
    // significaba recorrer 6 paginas a mano. La busqueda tiene que ir al
    // servidor -- filtrar la pagina ya descargada solo encontraria a quien ya
    // se esta viendo, que es justamente el caso en el que no se necesita
    // buscar. `busquedaAplicada` es la que el servidor ya respondio; se usa
    // para no re-disparar la consulta y para saber si hay filtro puesto.
    const [busqueda, setBusqueda] = useState("");
    const [busquedaAplicada, setBusquedaAplicada] = useState("");

    const handleEdit = (record) => {
        handleShowEdit(record)
    };

    const columnas = [
        {
            key: "full_name",
            titulo: "Nombre",
            render: (u) => <Celda valor={u.full_name ? tituloDeNombre(u.full_name) : "Sin determinar"} />,
        },
        { key: "user_email", titulo: "Email", ancho: 260, render: (u) => <Celda valor={u.user_email} /> },
        {
            key: "perfil",
            titulo: "Perfil",
            ancho: 220,
            render: (u) => (
                <Celda
                    valor={
                        u.rbac_role?.rol?.nombre ||
                        u.role?.nombre ||
                        u.rol?.nombre ||
                        u.Seguros?.nombre ||
                        "Sin rol RBAC"
                    }
                />
            ),
        },
        {
            key: "acciones",
            titulo: "",
            ancho: 60,
            alinear: "centro",
            render: (u) =>
                hasPermission("users.edit_personal") ? (
                    <MenuAcciones
                        titulo={`Acciones de ${u.full_name}`}
                        acciones={[{ label: "Editar", icono: IconoEditar, onClick: () => handleEdit(u) }]}
                    />
                ) : null,
        },
    ];

    const fetchInitialData = useCallback(async (page = 1, limit = 10, search = "") => {
        setLoading(true);
        const users = await Listarusers({page, limit, search});

        // El servicio no devuelve nada si la petición falla (su `catch` sólo
        // hace `console.log`), y aquí se leía `users.data` a secas: un fallo de
        // red reventaba la pestaña entera con un TypeError.
        if (users?.pagination) {
            setListarUsers(users.data);
            setError(null);
            setTotal(users.pagination.total);
            setCurrentPage(users.pagination.page);
            setPageSize(users.pagination.limit);
        } else {
            setListarUsers([]);
            setTotal(0);
            setError("No se pudo obtener el listado de personal.");
        }

        setBusquedaAplicada(search);
        setLoading(false);
    }, []);

    // Antes esta dependencia incluía `currentPage` y `pageSize`, que la propia
    // consulta escribe: cada cambio de página disparaba DOS peticiones
    // idénticas al backend.
    useEffect(() => {
        if (tabs.asesores) {
            fetchInitialData(1, pageSize, busqueda);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchInitialData, tabs.asesores, refreshKey]);

    // Una consulta por pausa de tecleo, no una por letra.
    useEffect(() => {
        if (!tabs.asesores) return;
        if (busqueda === busquedaAplicada) return;
        const t = setTimeout(() => {
            setCurrentPage(1);
            fetchInitialData(1, pageSize, busqueda);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busqueda, tabs.asesores, pageSize]);


    return (
        <>
            <div className="mb-3" style={{ maxWidth: 420 }}>
                <CampoTexto
                    etiqueta="Buscar personal"
                    icono={IconoBuscar}
                    placeholder="Nombre, correo o usuario"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    data-testid="usuarios-buscar"
                />
            </div>

            <DataTable
                columnas={columnas}
                filas={listarUsers}
                filaKey="id"
                cargando={loading}
                error={error}
                onReintentar={() => fetchInitialData(currentPage, pageSize, busquedaAplicada)}
                etiquetaTabla="Personal administrativo"
                hayFiltrosAplicados={Boolean(busquedaAplicada)}
                onLimpiarFiltros={() => setBusqueda("")}
                textoVacioSinFiltros="Todavía no hay personal registrado."
                paginacion={{
                    page: currentPage,
                    pageSize,
                    total,
                    tamanos: [10, 20, 50],
                    onCambio: (p, ps) => {
                        setCurrentPage(p);
                        setPageSize(ps);
                        fetchInitialData(p, ps, busquedaAplicada);
                    },
                }}
            />
        </>
    )
}


export default UserListado
